import React from 'react';
import {
  EmptyState,
  LoadingState,
  Modal,
  ModalCloseButton,
  ModalContent,
  StatusBadge,
  Typography,
} from '../common';

export const formatLeagueDate = (iso) => {
  if (!iso) return 'TBC';
  const date = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

export const leaguePlayerNameClass = (player) =>
  player?.sex_male === false || player?.sexMale === false ? 'text-female font-medium' : 'text-text-primary';

const DARK_SELECT =
  'min-h-[44px] rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50';

export const LeagueSeasonSelect = ({
  id = 'league-season-select',
  seasons,
  value,
  onChange,
  variant = 'default',
  className = '',
}) => {
  if (!seasons?.length) return null;
  const selectClass = variant === 'dark' ? `${DARK_SELECT} ${className}` : `form-input ${className}`;
  return (
    <>
      <label htmlFor={id} className="sr-only">Season</label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(event) => onChange(Number(event.target.value))}
        className={selectClass}
      >
        {seasons.map((season) => (
          <option key={season.id} value={season.start_year} className="text-text-primary">
            {season.label}
          </option>
        ))}
      </select>
    </>
  );
};

const playerDisplayName = (player) => player?.display_name || player?.name || '—';

const isTopFlightDivision = (name) =>
  /^division\s*1$/i.test(String(name ?? '').trim()) || /^premier/i.test(String(name ?? '').trim());

const tableZoneForRow = (position, teamCount, hasPromotion) => {
  if (hasPromotion && position === 1) return 'promote';
  if (hasPromotion && position === 2 && teamCount >= 4) return 'promote-playoff';
  if (position === teamCount && teamCount >= 2) return 'relegate';
  if (position === teamCount - 1 && teamCount >= 4) return 'relegate-playoff';
  return null;
};

const TABLE_ZONE = {
  promote: {
    row: 'bg-success-light',
    bar: 'border-l-success',
    label: 'Automatic promotion',
  },
  'promote-playoff': {
    row: '',
    bar: 'border-l-success',
    label: 'Promotion play-off',
  },
  'relegate-playoff': {
    row: 'bg-warning-light',
    bar: 'border-l-warning',
    label: 'Relegation play-off',
  },
  relegate: {
    row: 'bg-danger-light',
    bar: 'border-l-danger',
    label: 'Relegation',
  },
};

export const DivisionTable = ({ standings, highlightTeamId, teamsById, onSelectTeam, divisionName }) => {
  const teamCount = standings.length;
  const hasPromotion = !isTopFlightDivision(divisionName);

  return (
    <table className="w-full min-w-[20rem] text-left text-sm">
      <thead>
        <tr className="border-b border-border text-text-muted">
          <th className="border-l-4 border-l-transparent px-3 py-2 font-medium">#</th>
          <th className="px-3 py-2 font-medium">Team</th>
          <th className="px-3 py-2 text-right font-medium">P</th>
          <th className="px-3 py-2 text-right font-medium">W</th>
          <th className="px-3 py-2 text-right font-medium">L</th>
          <th className="px-3 py-2 text-right font-medium" title="Rubbers won">RW</th>
          <th className="px-3 py-2 text-right font-medium" title="Rubbers lost">RL</th>
          <th className="px-3 py-2 text-right font-medium">Pts</th>
        </tr>
      </thead>
      <tbody>
        {standings.map((row) => {
          const ours = row.team_id === highlightTeamId;
          const name = teamsById.get(row.team_id)?.name ?? 'Team';
          const Cell = onSelectTeam ? 'button' : 'span';
          const zone = tableZoneForRow(row.position, teamCount, hasPromotion);
          const zoneStyle = zone ? TABLE_ZONE[zone] : null;
          return (
            <tr
              key={row.team_id}
              className={[
                zoneStyle?.row ?? '',
                ours && !zone ? 'bg-primary-light' : '',
                ours ? 'font-medium text-text-primary' : 'text-text-secondary',
              ].filter(Boolean).join(' ')}
            >
              <td className={`border-l-4 px-3 py-2 tabular-nums ${zoneStyle?.bar ?? 'border-l-transparent'}`}>
                {row.position}
              </td>
              <td className="px-3 py-2">
                <Cell
                  type={onSelectTeam ? 'button' : undefined}
                  onClick={onSelectTeam ? () => onSelectTeam(row.team_id) : undefined}
                  className={onSelectTeam ? 'text-left hover:underline focus:outline-none focus:ring-2 focus:ring-primary' : undefined}
                >
                  {name}
                  {zoneStyle ? <span className="sr-only"> ({zoneStyle.label})</span> : null}
                </Cell>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{row.played}</td>
              <td className="px-3 py-2 text-right tabular-nums">{row.matches_won}</td>
              <td className="px-3 py-2 text-right tabular-nums">{row.matches_lost}</td>
              <td className="px-3 py-2 text-right tabular-nums">{row.rubbers_won ?? '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{row.rubbers_lost ?? '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{row.points_for}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const ResultRow = ({ fixture, teamId, teamsById, onOpen, viewerPlayed = false }) => {
  const isHome = fixture.home_team_id === teamId;
  const opponentId = isHome ? fixture.away_team_id : fixture.home_team_id;
  const opponent = teamsById.get(opponentId)?.name ?? 'Opponent';
  const us = isHome ? fixture.home_score : fixture.away_score;
  const them = isHome ? fixture.away_score : fixture.home_score;
  const won = us != null && them != null && us > them;
  const lost = us != null && them != null && us < them;
  const resultVariant = won ? 'success' : lost ? 'danger' : 'default';
  const resultLabel = won ? 'Won' : lost ? 'Lost' : '—';

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View match card, ${isHome ? 'vs' : 'at'} ${opponent}${viewerPlayed ? ', you played' : ''}`}
      className="group flex min-h-[44px] w-full items-center gap-3 rounded-2xl border border-border-medium bg-surface px-3 py-3 text-left shadow-brand-sm transition-all hover:shadow-brand-md focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <div className="w-16 shrink-0">
        <Typography variant="caption">{formatLeagueDate(fixture.match_date)}</Typography>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Typography variant="label" className="truncate">
            {isHome ? 'vs' : 'at'} {opponent}
          </Typography>
          {viewerPlayed ? (
            <span className="shrink-0 rounded-full bg-primary-light px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
              You
            </span>
          ) : null}
        </div>
        <Typography variant="caption">
          {isHome ? 'Home' : 'Away'}
          {fixture.match_time ? ` · ${fixture.match_time}` : ''}
        </Typography>
      </div>
      <Typography variant="strong" className="tabular-nums">
        {us ?? '–'}–{them ?? '–'}
      </Typography>
      <StatusBadge variant={resultVariant} size="small">{resultLabel}</StatusBadge>
      <svg
        className="h-4 w-4 shrink-0 text-text-muted transition-colors group-hover:text-primary"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

const FixtureSideLine = ({ name, score, isWinner }) => (
  <div className="flex items-center justify-between gap-3">
    <span className={`truncate text-sm ${isWinner ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
      {name}
    </span>
    <span className={`shrink-0 text-sm tabular-nums ${isWinner ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
      {score ?? '–'}
    </span>
  </div>
);

export const FixtureRow = ({ fixture, teamsById, onOpen }) => {
  const homeName = teamsById.get(fixture.home_team_id)?.name ?? 'Home';
  const awayName = teamsById.get(fixture.away_team_id)?.name ?? 'Away';
  const { home_score: homeScore, away_score: awayScore } = fixture;
  const hasScore = homeScore != null && awayScore != null;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View match card, ${homeName} ${homeScore ?? ''} v ${awayName} ${awayScore ?? ''}`.trim()}
      className="group flex min-h-[44px] w-full items-center gap-3 rounded-2xl border border-border-medium bg-surface px-3 py-3 text-left shadow-brand-sm transition-all hover:shadow-brand-md focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <div className="w-16 shrink-0">
        <Typography variant="caption">{formatLeagueDate(fixture.match_date)}</Typography>
        {fixture.match_time ? <Typography variant="caption" className="block">{fixture.match_time}</Typography> : null}
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <FixtureSideLine name={homeName} score={homeScore} isWinner={hasScore && homeScore > awayScore} />
        <FixtureSideLine name={awayName} score={awayScore} isWinner={hasScore && awayScore > homeScore} />
      </div>
      <svg
        className="h-4 w-4 shrink-0 text-text-muted transition-colors group-hover:text-primary"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

const formatLeagueFullDate = (iso) => {
  if (!iso) return 'TBC';
  const date = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const pairKey = (id1, id2) => [id1, id2].filter(Boolean).sort().join('|');

// Formats where every home pair plays every away pair (e.g. 3 × 3) render as a grid;
// anything else (8-rubber cards, placeholder walkover players) falls back to a list.
const buildRubberGrid = (rubbers) => {
  const rows = [];
  const cols = [];
  const cells = new Map();
  for (const rubber of rubbers) {
    const homeKey = pairKey(rubber.home_player_id_1, rubber.home_player_id_2);
    const awayKey = pairKey(rubber.away_player_id_1, rubber.away_player_id_2);
    if (!homeKey || !awayKey) return null;
    if (!rows.some((row) => row.key === homeKey)) {
      rows.push({ key: homeKey, ids: [rubber.home_player_id_1, rubber.home_player_id_2] });
    }
    if (!cols.some((col) => col.key === awayKey)) {
      cols.push({ key: awayKey, ids: [rubber.away_player_id_1, rubber.away_player_id_2] });
    }
    const cellKey = `${homeKey}::${awayKey}`;
    if (cells.has(cellKey)) return null;
    cells.set(cellKey, rubber);
  }
  if (rows.length < 2 || cols.length < 2 || rows.length * cols.length !== rubbers.length) return null;
  return { rows, cols, cells };
};

const PairNames = ({ ids, playersById, onDark = false, faded = false, className = '' }) => {
  const players = ids.map((id) => playersById.get(id)).filter(Boolean);
  const wrapperClass = `${className} ${faded ? 'opacity-50' : ''}`.trim();
  if (!players.length) return <span className={wrapperClass}>—</span>;
  return (
    <span className={wrapperClass}>
      {players.map((player, index) => {
        const female = player.sex_male === false;
        const tone = onDark ? (female ? 'text-fuchsia-300' : '') : leaguePlayerNameClass(player);
        return (
          <React.Fragment key={player.id}>
            {index > 0 ? ' / ' : null}
            <span className={`whitespace-nowrap ${tone}`}>{playerDisplayName(player)}</span>
          </React.Fragment>
        );
      })}
    </span>
  );
};

// text-sm line (1.25rem) + space-y-0.5 gap (0.125rem), so every cell reserves room for the longest rubber.
const gamesListMinHeight = (maxGames) => `${maxGames * 1.25 + (maxGames - 1) * 0.125}rem`;

const isOpposition = (ourSide, side) => Boolean(ourSide) && ourSide !== side;

const RubberOutcome = ({ rubber, playersById, maxGames = 1, ourSide }) => {
  const homeWon = rubber.winner === 'home';
  const winnerIds = homeWon
    ? [rubber.home_player_id_1, rubber.home_player_id_2]
    : [rubber.away_player_id_1, rubber.away_player_id_2];
  const overturned = Boolean(rubber.invalid_label) || (rubber.winner_on_court && rubber.winner_on_court !== rubber.winner);
  return (
    <div className="flex h-full flex-col gap-1">
      <ul
        className="space-y-0.5 text-center text-sm tabular-nums text-text-primary"
        style={{ minHeight: gamesListMinHeight(maxGames) }}
      >
        {rubber.games?.length ? (
          rubber.games.map((game) => (
            <li key={game.game}>
              {game.home} – {game.away}
            </li>
          ))
        ) : (
          <li className="text-text-muted">{rubber.result_text || '—'}</li>
        )}
      </ul>
      <div className="mt-auto flex justify-center pt-1">
        <StatusBadge
          variant={homeWon ? 'success' : 'info'}
          size="small"
          className="max-w-full flex-wrap justify-center gap-1 text-center leading-tight"
          title={homeWon ? 'Home win' : 'Away win'}
        >
          <span aria-hidden="true">👑</span>
          <span className="sr-only">{homeWon ? 'Home win by ' : 'Away win by '}</span>
          <PairNames
            ids={winnerIds}
            playersById={playersById}
            faded={isOpposition(ourSide, homeWon ? 'home' : 'away')}
          />
        </StatusBadge>
      </div>
      {overturned && (
        <Typography variant="caption" className="text-center text-warning-dark">
          {rubber.invalid_label || 'Result overturned'}
          {rubber.invalid_reason ? ` — ${rubber.invalid_reason}` : ''}
        </Typography>
      )}
    </div>
  );
};

const RubberCell = ({ header, rubber, playersById, maxGames, ourSide }) => (
  <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-border-medium bg-surface shadow-brand-sm">
    <div className="bg-secondary px-2 py-1.5 text-center text-[11px] font-semibold leading-tight text-text-inverse sm:text-xs">
      {header}
    </div>
    <div className="flex-1 px-2 py-2">
      {rubber
        ? <RubberOutcome rubber={rubber} playersById={playersById} maxGames={maxGames} ourSide={ourSide} />
        : <Typography variant="caption" className="text-center">—</Typography>}
    </div>
  </div>
);

const RubberGrid = ({ grid, playersById, maxGames, ourSide }) => (
  <div className="space-y-3">
    {grid.rows.map((row) => (
      <div
        key={row.key}
        className="rounded-2xl border border-border bg-surface-subtle p-2 sm:flex sm:items-stretch sm:gap-3 sm:p-3"
      >
        <div className="mb-2 flex items-center border-l-4 border-success pl-3 sm:mb-0 sm:w-32 sm:shrink-0">
          <PairNames
            ids={row.ids}
            playersById={playersById}
            faded={isOpposition(ourSide, 'home')}
            className="text-base font-semibold leading-snug text-text-primary sm:text-sm sm:font-medium"
          />
        </div>
        <div
          className="grid flex-1 gap-2"
          style={{ gridTemplateColumns: `repeat(${grid.cols.length}, minmax(0, 1fr))` }}
        >
          {grid.cols.map((col) => (
            <RubberCell
              key={col.key}
              header={<PairNames ids={col.ids} playersById={playersById} onDark faded={isOpposition(ourSide, 'away')} />}
              rubber={grid.cells.get(`${row.key}::${col.key}`)}
              playersById={playersById}
              maxGames={maxGames}
              ourSide={ourSide}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const RubberList = ({ rubbers, playersById, maxGames, ourSide }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {rubbers.map((rubber) => (
      <RubberCell
        key={rubber.id}
        header={
          <>
            <span className="block text-[10px] font-medium uppercase tracking-wide text-text-inverse/70">
              Rubber {rubber.rubber_number}
            </span>
            <PairNames
              ids={[rubber.home_player_id_1, rubber.home_player_id_2]}
              playersById={playersById}
              onDark
              faded={isOpposition(ourSide, 'home')}
            />
            <span className="text-text-inverse/70"> vs </span>
            <PairNames
              ids={[rubber.away_player_id_1, rubber.away_player_id_2]}
              playersById={playersById}
              onDark
              faded={isOpposition(ourSide, 'away')}
            />
          </>
        }
        rubber={rubber}
        playersById={playersById}
        maxGames={maxGames}
        ourSide={ourSide}
      />
    ))}
  </div>
);

const ScoreTeam = ({ name, accentClass, won }) => (
  <div className="flex min-w-0 flex-col items-center text-center">
    <span className={`text-lg leading-none ${won ? '' : 'invisible'}`} aria-hidden="true">👑</span>
    {won && <span className="sr-only">Winner: </span>}
    <Typography variant="h5" as="p" className="line-clamp-2 text-base font-bold sm:text-xl">
      {name}
    </Typography>
    <span className={`mt-2 h-1 w-16 rounded-full sm:w-20 ${accentClass}`} aria-hidden="true" />
  </div>
);

export const MatchCardModal = ({
  fixture,
  card,
  teamsById,
  competitionLabel,
  divisionName,
  ourTeamId,
  onClose,
}) => {
  const homeName = teamsById.get(fixture.home_team_id)?.name ?? 'Home';
  const awayName = teamsById.get(fixture.away_team_id)?.name ?? 'Away';
  const ourSide = ourTeamId === fixture.home_team_id ? 'home' : ourTeamId === fixture.away_team_id ? 'away' : null;
  const hasScore = fixture.home_score != null && fixture.away_score != null;
  const playersById = new Map((card?.players ?? []).map((player) => [player.id, player]));
  const rubbers = card?.rubbers ?? [];
  const grid = buildRubberGrid(rubbers);
  const maxGames = Math.max(1, ...rubbers.map((rubber) => rubber.games?.length || 1));
  const details = [competitionLabel, divisionName, formatLeagueFullDate(fixture.match_date)].filter(Boolean);

  return (
    <Modal isOpen onClose={onClose} layout="sheet">
      <ModalContent size="large" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
          <Typography variant="caption" as="h2" className="uppercase tracking-wide">
            Match card
          </Typography>
          <ModalCloseButton onClose={onClose} className="static shrink-0" />
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
            <ScoreTeam
              name={homeName}
              accentClass="bg-success"
              won={hasScore && fixture.home_score > fixture.away_score}
            />
            <p
              className="text-4xl font-extrabold tabular-nums text-text-primary sm:text-5xl"
              aria-label={hasScore ? `${homeName} ${fixture.home_score}, ${awayName} ${fixture.away_score}` : 'No score yet'}
            >
              {hasScore ? `${fixture.home_score} – ${fixture.away_score}` : 'vs'}
            </p>
            <ScoreTeam
              name={awayName}
              accentClass="bg-primary"
              won={hasScore && fixture.away_score > fixture.home_score}
            />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
            <Typography variant="caption" as="p" className="text-text-secondary">
              {details.join(' · ')}
            </Typography>
            {fixture.walkover_label ? (
              <StatusBadge variant="warning" size="small">{fixture.walkover_label}</StatusBadge>
            ) : null}
          </div>

          {!card ? (
            <LoadingState message="Loading match card…" />
          ) : !rubbers.length ? (
            <EmptyState title="No match card" description="This fixture only has a score, not a full card." />
          ) : (
            <section className="space-y-3">
              <div className="flex items-baseline justify-between gap-2">
                <Typography variant="h5" as="h3">Rubber results</Typography>
                <Typography variant="caption">
                  {grid ? `${grid.rows.length} × ${grid.cols.length}` : `${rubbers.length} rubbers`}
                </Typography>
              </div>
              {grid ? (
                <RubberGrid grid={grid} playersById={playersById} maxGames={maxGames} ourSide={ourSide} />
              ) : (
                <RubberList rubbers={rubbers} playersById={playersById} maxGames={maxGames} ourSide={ourSide} />
              )}
            </section>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

const SnapshotStatCell = ({ label, value }) => (
  <div className="min-w-0 flex flex-col items-center px-2 py-3 text-center sm:px-3 sm:py-3.5">
    <p className="truncate text-[9px] font-semibold uppercase tracking-wider text-white/60 leading-tight sm:text-[10px]">
      {label}
    </p>
    <p className="mt-1.5 text-xl font-bold tabular-nums leading-none text-white sm:text-2xl">
      {value ?? '—'}
    </p>
  </div>
);

export const SeasonSnapshot = ({ standing, leagueName, divisionName, title, header }) => {
  if (!standing && !header && !title) return null;
  const stats = standing
    ? [
      { label: 'Position', value: standing.position != null ? `${standing.position}` : '—' },
      { label: 'Played', value: standing.played ?? '—' },
      { label: 'Won–lost', value: `${standing.matches_won}–${standing.matches_lost}` },
      { label: 'Rubbers', value: `${standing.rubbers_won}–${standing.rubbers_lost}` },
      { label: 'Points', value: standing.points_for ?? '—' },
    ]
    : [];
  const meta = [leagueName, divisionName].filter(Boolean).join(' · ');

  return (
    <section className="overflow-hidden rounded-2xl bg-[#0e1624] shadow-brand-lg ring-1 ring-white/[0.06]">
      {(header || title || meta) && (
        <div className="px-4 pt-4 pb-3 sm:px-5">
          {header ?? (
            <>
              {title ? (
                <h2 className="truncate text-lg font-bold leading-tight text-white sm:text-xl">{title}</h2>
              ) : null}
              {meta ? (
                <p className={`text-sm text-white/70 ${title ? 'mt-1' : ''}`}>{meta}</p>
              ) : null}
            </>
          )}
        </div>
      )}
      {stats.length > 0 ? (
        <div className="grid grid-cols-3 divide-x divide-white/[0.1] border-t border-white/[0.1] sm:grid-cols-5">
          {stats.map((stat) => (
            <SnapshotStatCell key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </div>
      ) : null}
    </section>
  );
};
